"use client";

import { useState, useCallback, useEffect, useId, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import * as z from "zod";
import {
  Save,
  FileText,
  Calculator,
  CreditCard,
  Paperclip,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  X,
  CalendarIcon,
  DollarSign,
  User,
  Tag,
  Building,
  Calendar,
  Clock,
  FileImage,
  Archive
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toDateString, fromDateString } from "@/lib/utils/dateUtils";

import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BankLogo } from "@/components/ui/bank-logo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// Import FormModal
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Import existing sections
import { BasicInfoSection } from "./sections/BasicInfoSection";
import { CategorySection } from "./sections/CategorySection";
import { PaymentConditionsSection } from "./sections/PaymentConditionsSection";
import { AccountsSection } from "./AccountsSection";
import { AttachmentsSection } from "./sections/AttachmentsSection";
import { TransactionStatusSelector } from "./sections/TransactionStatusSelector";

// Import payment conditions (unified installment + recurrence)
import { PaymentConditions } from "./PaymentConditions";

// Import quick add modals
import { QuickAddClientModal } from "./QuickAddClientModal";
import { QuickAddSupplierModal } from "./QuickAddSupplierModal";
import { QuickAddCostCenterModal } from "./QuickAddCostCenterModal";
import { QuickAddPaymentAccountModal } from "./QuickAddPaymentAccountModal";

// Import hooks and data
import { useTransactions } from "@/hooks/useTransactions";
import { useRegisterData } from "./hooks/useRegisterData";
import { TransactionFormData, TransactionAttachment } from "@/types/transaction";
import { useIsMobile } from "@/hooks/useMobile";
import { TransactionErrorBoundary, useTransactionErrorHandler } from "./TransactionErrorBoundary";
import { EnhancedDatePicker } from "@/components/ui/date-picker";
import type { FileWithDescription } from "@/hooks/storage/useFileOperations";
import { supabase } from "@/integrations/supabase/client";

// Helper function to get first day of current month
const getFirstDayOfCurrentMonth = (): string => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString().split('T')[0];
};


// Helper functions for error handling and user feedback
function getErrorMessage(error: any): string {
  // Network errors
  if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
    return "Falha na conexão. Verifique sua internet e tente novamente.";
  }

  // Supabase specific errors
  if (error?.code) {
    switch (error.code) {
      case '23505': // Unique constraint violation
        return "Já existe uma transação com estes dados. Verifique e tente novamente.";
      case '23503': // Foreign key constraint violation
        return "Categoria ou centro de custo inválido. Verifique os campos selecionados.";
      case '23514': // Check constraint violation
        return "Valor inválido. Verifique os dados informados.";
      case 'PGRST204': // No rows found
        return "Registro não encontrado ou sem permissão de acesso.";
      case '42501': // Insufficient privilege
        return "Você não tem permissão para realizar esta operação.";
      default:
        return `Erro do banco de dados (${error.code}): ${error.message || 'Erro desconhecido'}`;
    }
  }

  // Authentication errors
  if (error?.message?.includes('JWT') || error?.message?.includes('auth')) {
    return "Sessão expirada. Faça login novamente.";
  }

  // Validation errors
  if (error?.message?.includes('Required') || error?.message?.includes('invalid')) {
    return "Dados inválidos. Verifique todos os campos obrigatórios.";
  }

  // File upload errors
  if (error?.message?.includes('upload') || error?.message?.includes('storage')) {
    return "Erro ao fazer upload de arquivos. Verifique o tamanho e formato dos anexos.";
  }

  // Default error with context
  return error?.message || "Erro ao salvar transação. Tente novamente.";
}

function showSavingMessage(isEditing: boolean) {
  const message = isEditing ? "Atualizando transação..." : "Criando transação...";
  toast.loading(message, { id: 'transaction-saving' });
}

function showSuccessMessage(isEditing: boolean) {
  const message = isEditing ? "Transação atualizada com sucesso!" : "Transação criada com sucesso!";
  toast.success(message, { id: 'transaction-saving' });
}

function showDetailedErrorMessage(error: any) {
  const message = getErrorMessage(error);
  toast.error(message, { id: 'transaction-saving', duration: 5000 });
}

// Unified schema for both forms - combining the best of both
// Normalize status coming from backend or UI to expected enum
function normalizeStatus(input: any): "PENDING" | "PAID" | "OVERDUE" | "CANCELLED" {
  const val = (typeof input === 'string' ? input : '').trim().toUpperCase();
  if (val === 'PAID') return 'PAID';
  if (val === 'OVERDUE') return 'OVERDUE';
  if (val === 'CANCELLED') return 'CANCELLED';
  return 'PENDING';
}
const unifiedTransactionSchema = z.object({
  id: z.string().optional(),
  type: z.enum(["INCOME", "EXPENSE"]),
  amount: z.coerce.number().min(0.01, "Valor deve ser maior que 0"),
  description: z.string().min(1, "Descrição é obrigatória"),
  date: z.string().min(1, "Data é obrigatória"),
  dueDate: z.string().optional(),
  categoryId: z.string().optional(),
  subcategoryId: z.string().optional(),
  accountId: z.string().optional(),
  paymentMethod: z.string().optional(),
  costCenterId: z.union([z.number(), z.string()]).optional(),
  // IDs podem vir como UUID ou numérico/string legada; manter flexível
  supplier_id: z.string().optional(),
  client_id: z.string().optional(),
  contactId: z.string().optional(), // Manter para compatibilidade durante transição
  installmentCount: z.coerce.number().min(1).default(1),
  installmentType: z.enum(["CASH", "INSTALLMENTS", "RECURRING"]).default("CASH"),
  total_installments: z.coerce.number().optional().superRefine((val, ctx) => {
    if (val === undefined || val === null || Number.isNaN(val)) return;
    if (val < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: 2,
        inclusive: true,
        type: "number",
        message: "Número mínimo de parcelas é 2",
      });
    } else if (val > 365) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: 365,
        inclusive: true,
        type: "number",
        message: "Máximo de 365 parcelas",
      });
    }
  }),
  tags: z.array(z.string()).default([]),
  attachments: z.array(z.object({
    file: z.any(),
    description: z.string()
  })).default([]),
  notes: z.string().optional(),
  status: z.enum(["PENDING", "PAID", "OVERDUE", "CANCELLED"]).default("PENDING"),
  competence_date: z.string().optional(),
  // Recurrence fields
  isRecurring: z.boolean().default(false),
  recurrenceFrequency: z.enum(["DAILY", "WEEKLY", "MONTHLY", "YEARLY"]).optional(),
  recurrenceInterval: z.coerce.number().int().min(1).max(365).optional(),
  recurrenceEndDate: z.string().optional(),
  recurrenceCount: z.coerce.number().int().min(1).max(365).optional(),
}).superRefine((val, ctx) => {
  // If recurrence is enabled, validate required fields
  const isRecurring = val.isRecurring || val.installmentType === "RECURRING";
  if (isRecurring) {
    if (!val.recurrenceFrequency) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: "string",
        received: "undefined",
        path: ["recurrenceFrequency"],
        message: "Selecione uma frequência para a recorrência",
      });
    }
    if (!val.recurrenceInterval || val.recurrenceInterval < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_type,
        expected: "number",
        received: val.recurrenceInterval,
        path: ["recurrenceInterval"],
        message: "Informe um intervalo válido (mínimo: 1)",
      });
    }
  }
});

type UnifiedTransactionSchema = z.infer<typeof unifiedTransactionSchema>;

function simplifyFormErrors(errors: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(errors || {}).map(([field, error]) => [
      field,
      {
        type: (error as any)?.type,
        message: (error as any)?.message,
        ref: (error as any)?.ref?.name,
      }
    ])
  );
}

function buildTransactionDebugLog({
  trigger,
  formData,
  isEditing,
  isSaving,
  isSaveDisabled,
  saveDisabledReason,
  hasMinimumFields,
  errors,
  attachments,
  existingAttachments,
  transaction
}: {
  trigger: string;
  formData: UnifiedTransactionSchema;
  isEditing: boolean;
  isSaving: boolean;
  isSaveDisabled: boolean;
  saveDisabledReason?: string;
  hasMinimumFields: boolean;
  errors: Record<string, any>;
  attachments: FileWithDescription[];
  existingAttachments: TransactionAttachment[];
  transaction?: any;
}) {
  const sanitizedValues = { ...formData };
  delete (sanitizedValues as any).attachments;

  const logPayload = {
    timestamp: new Date().toISOString(),
    trigger,
    mode: isEditing ? "edit" : "create",
    savingState: {
      isSaving,
      isSaveDisabled,
      hasMinimumFields,
      saveDisabledReason,
    },
    formValues: sanitizedValues,
    errors: simplifyFormErrors(errors),
    attachmentQueue: attachments.map((att, index) => ({
      index,
      name: att.file?.name,
      size: att.file?.size,
      type: att.file?.type,
      description: att.description,
      attachment_form: (att as any)?.attachment_form,
      attachment_type: (att as any)?.attachment_type,
    })),
    existingAttachments: existingAttachments.map(att => ({
      id: att.id,
      description: att.description,
      file_path: att.file_path,
    })),
    transactionProp: transaction
      ? {
        id: transaction.id,
        type: transaction.type,
        status: transaction.status,
      }
      : undefined,
    location: typeof window !== "undefined" ? window.location.href : undefined,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  };

  return [
    "----- Transaction Debug Log -----",
    JSON.stringify(logPayload, null, 2),
    "----- End Transaction Debug Log -----",
  ].join("\n");
}

interface UnifiedTransactionFormProps {
  transaction?: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: UnifiedTransactionSchema & {
    attachmentFiles?: { file: File; description: string; attachment_form?: any; attachment_type?: any }[];
    existingAttachmentUpdates?: { id: number; description?: string | null }[];
  }) => Promise<void>;
  isEditing?: boolean;
  isInstallmentChild?: boolean;
  installmentInfo?: {
    current: number;
    total: number;
    parentId: string;
  } | null;
}

export function UnifiedTransactionForm({
  transaction,
  isOpen,
  onClose,
  onSave,
  isEditing = false,
  isInstallmentChild = false,
  installmentInfo = null
}: UnifiedTransactionFormProps) {
  const rawFormId = useId();
  const formId = useMemo(
    () => `transaction-form-${rawFormId.replace(/:/g, "")}`,
    [rawFormId]
  );
    const deriveDisabledReason = (errors: any, values: UnifiedTransactionSchema, isSaving: boolean) => {
    if (isSaving) return "Salvando...";
    if (errors?.description) return "Preencha a descrição da transação";
    if (errors?.amount) return "Informe um valor maior que zero";
    if (!values.amount || values.amount <= 0) return "Informe um valor maior que zero";
    if (errors?.date || !values.date) return "Selecione a data principal";
    return Object.keys(errors || {}).length > 0 ? "Corrija os campos destacados" : undefined;
  };

  const [isSaving, setIsSaving] = useState(false);
  const [attachments, setAttachments] = useState<FileWithDescription[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<TransactionAttachment[]>([]);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);
  const [showAddPaymentAccountModal, setShowAddPaymentAccountModal] = useState(false);
  const isMobile = useIsMobile();

  // Get register data
  const { categories, costCenters, paymentAccounts, clients, suppliers, fetchCategories, fetchCostCenters, fetchPaymentAccounts, refetchClients } = useRegisterData();
  const { getTransactionById, getTransactionAttachments, deleteAttachment, downloadAttachment } = useTransactions();

  // Debug logs for clients data
  useEffect(() => {
    console.log('🔍 [UnifiedTransactionForm] Clients data:', {
      clients,
      count: clients?.length || 0,
      isLoading: !clients,
      timestamp: new Date().toISOString()
    });

    if (clients && clients.length > 0) {
      console.table(clients.slice(0, 3));
    }
  }, [clients]);

  const normalizeEmpty = <T,>(value: T | "" | null | undefined) =>
    value === "" || value === null ? undefined : value;

  const form = useForm<UnifiedTransactionSchema>({
    resolver: zodResolver(unifiedTransactionSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      type: "EXPENSE",
      amount: 0,
      description: "",
      date: undefined,
      dueDate: undefined,
      competence_date: undefined,
      categoryId: undefined,
      accountId: undefined,
      paymentMethod: undefined,
      costCenterId: undefined,
      installmentCount: 1,
      installmentType: "CASH",
      total_installments: undefined,
      tags: [],
      attachments: [],
      notes: "",
      status: "PENDING",
      isRecurring: false,
      recurrenceFrequency: undefined,
      recurrenceInterval: undefined,
      recurrenceEndDate: undefined,
      recurrenceCount: undefined,
    },
  });

  // Watch form changes for real-time preview
  const formData = form.watch();
  const transactionType = formData.type;
  const isRecurring = formData.isRecurring;
  const saveDisabledReason = deriveDisabledReason(form.formState.errors, formData, isSaving);

  // Improved validation logic that matches the disabled reason logic
  const hasMinimumFields =
    Boolean(formData.description && formData.description.trim().length > 0) &&
    typeof formData.amount === "number" &&
    formData.amount > 0 &&
    Boolean(formData.date) &&
    Object.keys(form.formState.errors || {}).length === 0;

  const isSaveDisabled = isSaving || !hasMinimumFields || !!saveDisabledReason;
  const recurrenceFrequency = formData.recurrenceFrequency;
  const recurrenceInterval = formData.recurrenceInterval;
  const recurrenceCount = formData.recurrenceCount;
  const recurrenceEndDate = formData.recurrenceEndDate;
  


  const captureAndCopyDebugLog = useCallback(async (trigger: string) => {
    const logText = buildTransactionDebugLog({
      trigger,
      formData: form.getValues(),
      isEditing,
      isSaving,
      isSaveDisabled,
      saveDisabledReason,
      hasMinimumFields,
      errors: form.formState.errors,
      attachments,
      existingAttachments,
      transaction
    });

    console.log(logText);
    if (typeof window !== "undefined") {
      (window as any).__lastTransactionDebugLog = logText;
    }

    const canUseClipboard =
      typeof navigator !== "undefined" && !!navigator.clipboard?.writeText;

    if (canUseClipboard) {
      try {
        await navigator.clipboard.writeText(logText);
        toast.success("Log de debug copiado. Basta colar aqui no chat.");
        return;
      } catch (error) {
        console.warn("Não foi possível copiar o log automaticamente", error);
      }
    }

    toast.info("Log gerado no console (window.__lastTransactionDebugLog).");
  }, [attachments, existingAttachments, form, hasMinimumFields, isEditing, isSaveDisabled, isSaving, saveDisabledReason, transaction]);

  // Error handling hook must run before any hook-dependent callbacks
  const { handleTransactionError } = useTransactionErrorHandler();

  // Reset form to defaults when opening for new transaction
  useEffect(() => {
    if (isOpen && !isEditing && !transaction) {
      form.reset({
        type: "EXPENSE",
        amount: 0,
        description: "",
        date: new Date().toISOString().split('T')[0],
        dueDate: new Date().toISOString().split('T')[0],
        competence_date: getFirstDayOfCurrentMonth(),
        categoryId: undefined,
        accountId: undefined,
        paymentMethod: undefined,
        costCenterId: undefined,
        installmentCount: 1,
        installmentType: "CASH",
        total_installments: undefined,
        tags: [],
        attachments: [],
        notes: "",
        status: "PENDING",
        isRecurring: false,
        recurrenceFrequency: undefined,
        recurrenceInterval: undefined,
        recurrenceEndDate: undefined,
        recurrenceCount: undefined,
      });
    }
  }, [isOpen, isEditing, transaction, form]);

  // Load existing attachments from DB when editing
  useEffect(() => {
    const loadExistingAttachments = async () => {
      if (!isOpen) return;
      const txId = transaction?.id;
      if (!txId) return;
      try {
        const { data, error } = await supabase
          .from("transaction_attachments")
          .select("*")
          .eq("transaction_id", txId)
          .order("created_at", { ascending: false });
        if (error) throw error;
        setExistingAttachments((data || []) as TransactionAttachment[]);
      } catch (err) {
        console.warn("Não foi possível carregar anexos existentes:", err);
      }
    };
    void loadExistingAttachments();
  }, [isOpen, transaction?.id]);

  // Load transaction data when cloning (create with prefilled data)
  useEffect(() => {
    if (transaction && !isEditing) {
      // Helper to safely extract YYYY-MM-DD without timezone shift
      const safeExtractDate = (val: any): string | undefined => {
        if (!val) return undefined;
        if (typeof val === 'string') return val.split('T')[0];
        if (val instanceof Date) return val.toISOString().split('T')[0];
        return undefined;
      };

      const normalizedDueDate = safeExtractDate(
        (transaction as any).dueDate ??
        (transaction as any).due_date ??
        (transaction as any).payment_date
      );
      const normalizedDate = safeExtractDate((transaction as any).date);
      const normalizedCompetenceDate = safeExtractDate(
        (transaction as any).competenceDate ??
        (transaction as any).competence_date
      );

      const normalizedInstallments = (transaction as any).total_installments && (transaction as any).total_installments >= 1
        ? (transaction as any).total_installments
        : 1;
      const normalizedTotalInstallments = (transaction as any).total_installments && (transaction as any).total_installments >= 2
        ? (transaction as any).total_installments
        : undefined;

      const formData = {
        ...transaction,
        id: undefined, // ensure new record
        isPaid: false,
        date: normalizedDate,
        dueDate: normalizedDueDate,
        competence_date: normalizedCompetenceDate,
        categoryId: (transaction as any).categoryId ?? (transaction as any).category_id ?? undefined,
        accountId: (transaction as any).accountId ?? (transaction as any).payment_account_id ?? undefined,
        costCenterId: ((transaction as any).costCenterId ?? (transaction as any).cost_center_id)?.toString(),
        paymentMethod: (transaction as any).paymentMethod ?? (transaction as any).payment_method ?? undefined,
        contactId: (transaction as any).contactId ?? undefined,
        supplier_id: (transaction as any).supplier_id ?? undefined,
        client_id: (transaction as any).client_id ?? undefined,
        installmentCount: normalizedInstallments,
        // For cloning, use the original transaction's installment type logic
        installmentType: normalizedTotalInstallments && normalizedTotalInstallments >= 2 ? "INSTALLMENTS" : "CASH",
        total_installments: normalizedTotalInstallments,
        // Always set to PENDING on clone; normalize any backend-provided values just in case
        status: 'PENDING',
      } as any;

      form.reset(formData);
      void form.trigger();

      if ((transaction as any).attachments && (transaction as any).attachments.length > 0) {
        setExistingAttachments((transaction as any).attachments as any);
      }
    }
  }, [transaction, isEditing, form]);

  // Load transaction data when editing
  useEffect(() => {
    if (transaction && isEditing) {
            // Helper to safely extract YYYY-MM-DD without timezone shift
      const safeExtractDate = (val: any): string | undefined => {
        if (!val) return undefined;
        if (typeof val === 'string') return val.split('T')[0];
        if (val instanceof Date) return val.toISOString().split('T')[0];
        return undefined;
      };

      // Normalize fields without aggressive defaults or fallbacks
      const normalizedDueDate = safeExtractDate(
        (transaction as any).dueDate ??
        transaction.due_date ??
        transaction.payment_date
      );
      const normalizedDate = safeExtractDate(transaction.date);
      const normalizedCompetenceDate = safeExtractDate(
        (transaction as any).competenceDate ??
        transaction.competence_date
      );

      // Map backend field names to form field names
      const normalizedInstallments = transaction.total_installments && transaction.total_installments >= 1
        ? transaction.total_installments
        : 1;
      const normalizedTotalInstallments = transaction.total_installments && transaction.total_installments >= 2
        ? transaction.total_installments
        : undefined;

      const formData = {
        ...transaction,
        date: normalizedDate, // Will be undefined if transaction.date is null, avoiding "today" default
        categoryId: normalizeEmpty(transaction.category_id || transaction.categoryId),
        accountId: normalizeEmpty(transaction.payment_account_id || transaction.accountId),
        costCenterId: normalizeEmpty((transaction.cost_center_id ?? transaction.costCenterId)?.toString()),
        paymentMethod: normalizeEmpty(transaction.payment_method || transaction.paymentMethod),

        contactId: normalizeEmpty(transaction.contactId), // Use the contactId already set by TransactionFormContainer
        supplier_id: normalizeEmpty(transaction.supplier_id),
        client_id: normalizeEmpty(transaction.client_id),
        dueDate: normalizedDueDate,
        competence_date: normalizedCompetenceDate,
        installmentCount: normalizedInstallments,
        // When editing an installment child, set INSTALLMENTS type regardless of total_installments
        installmentType: isInstallmentChild ? "INSTALLMENTS" : (normalizedTotalInstallments && normalizedTotalInstallments >= 2 ? "INSTALLMENTS" : "CASH"),
        total_installments: normalizedTotalInstallments,
        status: normalizeStatus(transaction.status || (transaction.is_paid ? "PAID" : "PENDING")),

      };

      const finalStatus = (transaction.status && transaction.status.trim() !== "") ? transaction.status : (transaction.is_paid ? "PAID" : "PENDING");
          
      form.reset(formData);
      void form.trigger();

      if (transaction.attachments && transaction.attachments.length > 0) {
        setExistingAttachments(transaction.attachments);
      }
    }
  }, [transaction, isEditing, form]);

  const handleSubmit = useCallback(async (data: UnifiedTransactionSchema) => {
    
    setIsSaving(true);

    // Show immediate feedback that saving has started
    showSavingMessage(isEditing);

    try {
      const attachmentFiles = attachments.map(att => ({
        file: att.file,
        description: att.description,
        attachment_form: att.attachment_form || 'receipt',
        attachment_type: att.attachment_type || 'document',
      }));

      const existingAttachmentUpdates = existingAttachments.map(att => ({
        id: Number(att.id),
        description: att.description || null,
      }));

      const payload = {
        ...data,
        attachmentFiles,
        existingAttachmentUpdates,
      };

      await onSave(payload);

      // Show success message
      showSuccessMessage(isEditing);

      // Clear form state after successful save
      setAttachments([]);
      setExistingAttachments([]);
      onClose();

      // Reset form after modal closes to avoid UI flicker
      setTimeout(() => {
        form.reset();
      }, 100);
    } catch (error) {
      console.error("Error saving transaction:", error);
      if (error instanceof Error) {
        handleTransactionError(error, 'transaction_save');
      } else {
        handleTransactionError(new Error(String(error)), 'transaction_save');
      }
      showDetailedErrorMessage(error);
    } finally {
      setIsSaving(false);
    }
  }, [onSave, isEditing, attachments, existingAttachments, handleTransactionError, form, onClose]);

  const handleDeleteExistingAttachment = async (attachment: TransactionAttachment) => {
    try {
      await deleteAttachment(attachment.id);
      setExistingAttachments(prev => prev.filter(a => a.id !== attachment.id));
      toast.success("Anexo excluído com sucesso!");
    } catch (error) {
      console.error("Error deleting attachment:", error);
      toast.error("Erro ao excluir anexo.");
    }
  };

  const handleDownloadAttachment = async (attachment: TransactionAttachment) => {
    try {
      await downloadAttachment(attachment);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      toast.error("Erro ao baixar anexo.");
    }
  };

  const handleExistingAttachmentDescriptionChange = (id: string, description: string) => {
    setExistingAttachments(prev => prev.map(att => (String(att.id) === id ? { ...att, description } as TransactionAttachment : att)));
  };

  // Funções para lidar com adição de clientes/fornecedores
  const handleClientAdded = useCallback(async (clientId: string) => {
    // Forçar refetch dos clientes para garantir dados atualizados
    try {
      await refetchClients();

      // Aguardar um pouco para garantir que o cache seja atualizado
      setTimeout(() => {
      }, 100);
    } catch (error) {
    }

    // Definir o cliente recém-criado no formulário
    form.setValue("client_id", clientId);
    form.setValue("contactId", clientId);
    void form.trigger();
  }, [form, refetchClients]);

  const handleSupplierAdded = (supplierId: string) => {
    // Define o fornecedor recém-criado no formulário
    form.setValue("supplier_id", supplierId);
    form.setValue("contactId", supplierId);
    void form.trigger();
  };

  const handleCostCenterAdded = useCallback((costCenterId: number) => {
    // Refresh cost centers list
    fetchCostCenters();
    // Define o centro de custo recém-criado no formulário
    form.setValue("costCenterId", costCenterId);
    void form.trigger();
  }, [fetchCostCenters, form]);

  const handlePaymentAccountAdded = useCallback((paymentAccountId: string) => {
    // Refresh payment accounts list
    fetchPaymentAccounts();
    // Define a conta de pagamento recém-criada no formulário
    form.setValue("accountId", paymentAccountId);
    void form.trigger();
  }, [fetchPaymentAccounts, form]);

  const handleDateChange = (fieldName: "date" | "dueDate" | "competence_date") => (value: string | Date | null | undefined) => {
    if (!value) {
      form.setValue(fieldName, undefined as any, { shouldValidate: true, shouldDirty: true });
      void form.trigger(fieldName);
      return;
    }

    const stringValue = typeof value === "string" ? value : toDateString(value as Date);
    form.setValue(fieldName, stringValue as any, { shouldValidate: true, shouldDirty: true });
    void form.trigger(fieldName);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="transaction-modal-responsive p-0">
        <Form {...form}>
          <div className="flex flex-col h-full bg-background">
            {/* Header */}
            <div className="px-8 py-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="grid grid-cols-3 items-center">
              {/* Left Section - Icon and Title */}
              <div className="flex items-center gap-4">
                <div className={cn(
                  "p-3 rounded-xl shadow-lg",
                  transactionType === "INCOME"
                    ? "bg-gradient-to-br from-green-500 to-green-600 text-white"
                    : "bg-gradient-to-br from-red-500 to-red-600 text-white"
                )}>
                  {transactionType === "INCOME" ? (
                    <TrendingUp className="h-8 w-8" />
                  ) : (
                    <TrendingDown className="h-8 w-8" />
                  )}
                </div>
                <div>
                  <DialogTitle className="text-3xl font-bold text-foreground">
                    {isEditing ? "Editar Transação" : (transaction ? "Clonar Transação" : "Nova Transação")}
                  </DialogTitle>
                  <DialogDescription className="text-lg text-muted-foreground mt-1">
                    Preencha as informações da transação financeira
                  </DialogDescription>
                </div>
              </div>

              {/* Center Section - Transaction Type Selector */}
              <div className="justify-center">
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="grid grid-cols-2 gap-4">
                          <Button
                            type="button"
                            variant={field.value === "EXPENSE" ? "default" : "outline"}
                            className={cn(
                              "h-12 text-base font-semibold rounded-lg transition-all duration-200 border-2",
                              field.value === "EXPENSE"
                                ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white border-red-500 shadow-md"
                                : "hover:border-red-500 hover:text-red-600 hover:bg-red-50 border-border"
                            )}
                            onClick={() => field.onChange("EXPENSE")}
                          >
                            <TrendingDown className="mr-2 h-5 w-5" />
                            Despesa
                          </Button>
                          <Button
                            type="button"
                            variant={field.value === "INCOME" ? "default" : "outline"}
                            className={cn(
                              "h-12 text-base font-semibold rounded-lg transition-all duration-200 border-2",
                              field.value === "INCOME"
                                ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-green-500 shadow-md"
                                : "hover:border-green-500 hover:text-green-600 hover:bg-green-50 border-border"
                            )}
                            onClick={() => field.onChange("INCOME")}
                          >
                            <TrendingUp className="mr-2 h-5 w-5" />
                            Receita
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Right Section - Action Buttons */}
              <div className="flex items-center gap-3 justify-end" title={saveDisabledReason}>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onClose}
                disabled={isSaving}
                className="h-12 px-8 text-base font-medium"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                form={formId}
                disabled={isSaveDisabled}
                aria-disabled={isSaveDisabled}
                title={saveDisabledReason}
                size="lg"
                className="h-12 px-8 text-base font-medium shadow-lg"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    {isEditing ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-hidden">
            <TransactionErrorBoundary
              onRetry={() => {
                form.reset();
                setAttachments([]);
                setExistingAttachments([]);
                setIsSaving(false);
              }}
            >
              <form id={formId} onSubmit={(e) => {
                  e.preventDefault();
                  void captureAndCopyDebugLog("form-submit");
                  form.handleSubmit(handleSubmit)(e);
                }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
                      e.preventDefault();
                    }
                  }}
                  className="h-full">
                  <ScrollArea className="h-full px-6 py-4">
                    <div className="max-w-none space-y-4">
                      {/* Main Content Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column - Main Information */}
                        <div className="space-y-4">

                          {/* Description & Amount */}
                          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                            <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm lg:col-span-8">
                              <CardContent className="p-4">
                                <FormField
                                  control={form.control}
                                  name="description"
                                  render={({ field }) => (
                                    <FormItem className="space-y-2">
                                      <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                        <FileText className="h-4 w-4 text-primary" />
                                        Descrição
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          placeholder={transactionType === "EXPENSE" ? "Ex: Conta de luz, Aluguel..." : "Ex: Salário, Freelance..."}
                                          {...field}
                                          className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20 transition-all"
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </CardContent>
                            </Card>

                            <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm lg:col-span-4">
                              <CardContent className="p-4">
                                <FormField
                                  control={form.control}
                                  name="amount"
                                  render={({ field }) => (
                                    <FormItem className="space-y-2">
                                      <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                        <DollarSign className="h-4 w-4 text-primary" />
                                        Valor (R$)
                                      </FormLabel>
                                      <FormControl>
                                        <div className="relative">
                                          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground font-bold text-sm">
                                            R$
                                          </div>
                                          <Input
                                            type="text"
                                            inputMode="decimal"
                                            placeholder="0,00"
                                            {...field}
                                            onChange={(e) => {
                                              const value = e.target.value.replace(/\D/g, '');
                                              const number = parseFloat(value) / 100;
                                              field.onChange(number);
                                            }}
                                            value={field.value ? field.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : ''}
                                            className="h-10 text-sm font-bold pl-10 pr-3 rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20 transition-all"
                                          />
                                        </div>
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </CardContent>
                            </Card>
                          </div>

                          {/* Category, Client/Supplier */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                              <CardContent className="p-4">
                                <FormField
                                  control={form.control}
                                  name="categoryId"
                                  render={({ field }) => (
                                    <FormItem className="space-y-2">
                                      <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                        <Tag className="h-4 w-4 text-primary" />
                                        Categoria
                                      </FormLabel>
                                      <Select
                                        onValueChange={field.onChange}
                                        value={field.value?.toString() || "none"}
                                      >
                                        <FormControl>
                                          <SelectTrigger className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20">
                                            <SelectValue placeholder="Selecione uma categoria" />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          <SelectItem value="none">Nenhuma</SelectItem>
                                          {categories
                                            .filter(cat => cat.type === transactionType.toLowerCase())
                                            .map((category) => (
                                              <SelectItem key={category.id} value={category.id.toString()}>
                                                {category.name}
                                              </SelectItem>
                                            ))}
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </CardContent>
                            </Card>

                            <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                              <CardContent className="p-4">
                                {transactionType === "INCOME" ? (
                                  <FormField
                                    control={form.control}
                                    name="client_id"
                                    render={({ field }) => (
                                      <FormItem className="space-y-2">
                                        <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                          <User className="h-4 w-4 text-primary" />
                                          Cliente
                                        </FormLabel>
                                        <Select
                                          onValueChange={(value) => {
                                            console.log('Client Select onValueChange:', { value, clientsCount: clients?.length });
                                            if (value === "add-new") {
                                              setShowAddClientModal(true);
                                            } else {
                                              field.onChange(value);
                                            }
                                          }}
                                          value={field.value || "none"}
                                        >
                                          <FormControl>
                                            <SelectTrigger
                                              className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20 relative z-10"
                                              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                                            >
                                              {(() => {
                                                const val = field.value;
                                                const isNone = !val || val === "none";

                                                if (isNone) {
                                                  return <SelectValue placeholder="Selecione o cliente" style={{ pointerEvents: 'none' }} />;
                                                }

                                                const selectedClient = clients?.find(c => c.id === val);

                                                return (
                                                  <div className="flex items-center gap-2">
                                                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                                      <User className="h-3.5 w-3.5 text-primary" />
                                                    </div>
                                                    <span className="truncate">{selectedClient ? selectedClient.name : val}</span>
                                                  </div>
                                                );
                                              })()}
                                            </SelectTrigger>
                                          </FormControl>
                                          <SelectContent>
                                            <SelectItem value="none">Nenhum</SelectItem>
                                            {clients?.map((client) => (
                                              <SelectItem key={client.id} value={client.id}>
                                                <div className="flex items-center gap-2">
                                                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                                    <User className="h-3.5 w-3.5 text-primary" />
                                                  </div>
                                                  <span className="truncate">{client.name}</span>
                                                </div>
                                              </SelectItem>
                                            ))}
                                            <SelectItem value="add-new" className="text-primary font-semibold">
                                              + Adicionar Novo Cliente...
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                ) : (
                                  <FormField
                                    control={form.control}
                                    name="supplier_id"
                                    render={({ field }) => (
                                      <FormItem className="space-y-2">
                                        <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                          <User className="h-4 w-4 text-primary" />
                                          Fornecedor
                                        </FormLabel>
                                        <Select
                                          onValueChange={(value) => {
                                            console.log('Supplier Select onValueChange:', { value, suppliersCount: suppliers?.length });
                                            if (value === "add-new") {
                                              setShowAddSupplierModal(true);
                                            } else {
                                              field.onChange(value);
                                            }
                                          }}
                                          value={field.value || "none"}
                                        >
                                                                                    <FormControl>
                                            <SelectTrigger
                                              className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20 relative z-10"
                                              style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                                            >
                                              {(() => {
                                                const val = field.value;
                                                const isNone = !val || val === "none";

                                                if (isNone) {
                                                  return <SelectValue placeholder="Selecione o fornecedor" style={{ pointerEvents: 'none' }} />;
                                                }

                                                const selectedSupplier = suppliers?.find(c => c.id === val);

                                                return (
                                                  <div className="flex items-center gap-2">
                                                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                                      <User className="h-3.5 w-3.5 text-primary" />
                                                    </div>
                                                    <span className="truncate">{selectedSupplier ? selectedSupplier.name : val}</span>
                                                  </div>
                                                );
                                              })()}
                                            </SelectTrigger>
                                          </FormControl>
                                          <SelectContent>
                                            <SelectItem value="none">Nenhum</SelectItem>
                                            {suppliers?.map((supplier) => (
                                              <SelectItem key={supplier.id} value={supplier.id}>
                                                <div className="flex items-center gap-2">
                                                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                                    <User className="h-3.5 w-3.5 text-primary" />
                                                  </div>
                                                  <span className="truncate">{supplier.name}</span>
                                                </div>
                                              </SelectItem>
                                            ))}
                                            <SelectItem value="add-new" className="text-primary font-semibold">
                                              + Adicionar Novo Fornecedor...
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                )}
                              </CardContent>
                            </Card>
                          </div>

  
                          {/* Financial Details */}
                          <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                            <CardHeader className="p-4 pb-2">
                              <CardTitle className="text-lg font-bold flex items-center gap-2">
                                <Calculator className="h-5 w-5 text-primary" />
                                Detalhes Financeiros
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 pt-2">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <FormField
                                  control={form.control}
                                  name="paymentMethod"
                                  render={({ field }) => (
                                    <FormItem className="space-y-2">
                                      <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                        <CreditCard className="h-3.5 w-3.5" />
                                        Método
                                      </FormLabel>
                                      {(() => {
                                        const paymentMethods = [
                                          { value: "CASH", label: "Dinheiro" },
                                          { value: "CREDIT_CARD", label: "Cartão de Crédito" },
                                          { value: "DEBIT_CARD", label: "Cartão de Débito" },
                                          { value: "BANK_TRANSFER", label: "Transferência Bancária" },
                                          { value: "PIX", label: "PIX" },
                                          { value: "BOLETO", label: "Boleto" },
                                          { value: "OTHER", label: "Outro" },
                                        ];

                                        const val = field.value || "";
                                        // Try strict match first, then case-insensitive
                                        const exactMatch = paymentMethods.find(m => m.value === val);
                                        const looseMatch = !exactMatch && val ? paymentMethods.find(m => m.value.toUpperCase() === val.toUpperCase()) : null;
                                        const effectiveValue = exactMatch ? exactMatch.value : (looseMatch ? looseMatch.value : val);

                                        return (
                                          <Select
                                            onValueChange={field.onChange}
                                            value={effectiveValue}
                                          >
                                            <FormControl>
                                              <SelectTrigger className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20">
                                                <SelectValue placeholder="Selecione">
                                                  {effectiveValue ? (
                                                    (paymentMethods.find(m => m.value === effectiveValue)?.label) || effectiveValue
                                                  ) : (
                                                    "Selecione"
                                                  )}
                                                </SelectValue>
                                              </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                              {paymentMethods.map((method) => (
                                                <SelectItem key={method.value} value={method.value}>
                                                  {method.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        );
                                      })()}
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <FormField
                                  control={form.control}
                                  name="costCenterId"
                                  render={({ field }) => (
                                    <FormItem className="space-y-2">
                                      <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                        <Building className="h-3.5 w-3.5" />
                                        Centro de Custo
                                      </FormLabel>
                                      <Select
                                        onValueChange={(value) => {
                                          if (value === "add-new") {
                                            setShowAddCostCenterModal(true);
                                          } else {
                                            field.onChange(value?.toString() || "");
                                          }
                                        }}
                                        value={(field.value || "").toString()}
                                      >
                                        <FormControl>
                                          <SelectTrigger className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20">
                                            {(() => {
                                              const val = field.value;
                                              if (!val) return <SelectValue placeholder="Selecione" />;

                                              const selectedCenter = costCenters.find(c => String(c.id) === String(val));

                                              return selectedCenter ? (
                                                <div className="flex items-center gap-2">
                                                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                                    <Building className="h-3.5 w-3.5 text-primary" />
                                                  </div>
                                                  <span className="truncate">{selectedCenter.name}</span>
                                                </div>
                                              ) : (
                                                <span className="text-muted-foreground">{val ? "Carregando..." : "Selecione"}</span>
                                              );
                                            })()}
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          {costCenters.map((center) => (
                                            <SelectItem key={center.id} value={center.id.toString()}>
                                              <div className="flex items-center gap-2">
                                                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                                                  <Building className="h-3.5 w-3.5 text-primary" />
                                                </div>
                                                <span className="truncate">{center.name}</span>
                                              </div>
                                            </SelectItem>
                                          ))}
                                          <SelectItem
                                            value="add-new"
                                            className="text-primary font-semibold"
                                            onClick={() => setShowAddCostCenterModal(true)}
                                          >
                                            + Adicionar Novo Centro de Custo...
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <FormField
                                  control={form.control}
                                  name="accountId"
                                  render={({ field }) => (
                                    <FormItem className="space-y-2">
                                      <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                        <Archive className="h-3.5 w-3.5" />
                                        Conta
                                      </FormLabel>
                                      <Select
                                        onValueChange={(value) => {
                                          if (value === "add-new") {
                                            setShowAddPaymentAccountModal(true);
                                          } else {
                                            field.onChange(value);
                                          }
                                        }}
                                        value={field.value}
                                      >
                                        <FormControl>
                                          <SelectTrigger className="h-10 text-sm rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20">
                                            {field.value ? (() => {
                                              const selectedAccount = paymentAccounts.find(acc => acc.id.toString() === field.value);
                                              console.log('🔍 BankLogo Debug - Selected Account:', {
                                                accountId: field.value,
                                                selectedAccount,
                                                bankName: selectedAccount?.bank_name,
                                                paymentAccountsCount: paymentAccounts.length
                                              });
                                              return selectedAccount ? (
                                                <div className="flex items-center gap-2">
                                                  <BankLogo
                                                    bankName={selectedAccount.bank_name || ''}
                                                    size="small"
                                                    className="shrink-0"
                                                  />
                                                  <span className="truncate">{selectedAccount.name}</span>
                                                </div>
                                              ) : null;
                                            })() : <SelectValue placeholder="Selecione" />}
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          {paymentAccounts.map((account) => (
                                            <SelectItem key={account.id} value={account.id.toString()}>
                                              <div className="flex items-center gap-2">
                                                <BankLogo
                                                  bankName={account.bank_name || ''}
                                                  size="small"
                                                  className="shrink-0"
                                                />
                                                <span className="truncate">{account.name}</span>
                                              </div>
                                            </SelectItem>
                                          ))}
                                          <SelectItem
                                            value="add-new"
                                            className="text-primary font-semibold"
                                            onClick={() => setShowAddPaymentAccountModal(true)}
                                          >
                                            + Adicionar Nova Conta de Pagamento...
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                            </CardContent>
                          </Card>

                          {/* Payment Conditions (unified: cash, installments, recurring) */}
                          <PaymentConditions
                            amount={formData.amount}
                            transactionType={transactionType}
                            readonly={isInstallmentChild}
                            installmentInfo={installmentInfo}
                          />
                        </div>

                        {/* Right Column - Dates & Status */}
                        <div className="space-y-4">
                          {/* Dates Card */}
                          <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                            <CardHeader className="p-4 pb-2">
                              <CardTitle className="text-lg font-bold flex items-center gap-2">
                                <Calendar className="h-5 w-5 text-primary" />
                                Datas
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 pt-2">
                              <div className="grid grid-cols-1 gap-4">
                                <FormField
                                  control={form.control}
                                  name="date"
                                  render={({ field }) => (
                                    <FormItem className="space-y-2">
                                      <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                        <Clock className="h-3.5 w-3.5 text-blue-500" />
                                        {transactionType === 'EXPENSE' ? 'Data Pagto' : 'Data Recebto'}
                                      </FormLabel>
                                      <FormControl>
                                        <EnhancedDatePicker
                                          value={field.value}
                                          onChange={handleDateChange("date")}
                                          placeholder="Selecione"
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <div className="grid grid-cols-2 gap-3">
                                  <FormField
                                    control={form.control}
                                    name="dueDate"
                                    render={({ field }) => (
                                      <FormItem className="space-y-2">
                                        <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                          <CalendarIcon className="h-3.5 w-3.5 text-orange-500" />
                                          Vencimento
                                        </FormLabel>
                                        <FormControl>
                                          <EnhancedDatePicker
                                            value={field.value}
                                            onChange={handleDateChange("dueDate")}
                                            placeholder="Selecione"
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />

                                  <FormField
                                    control={form.control}
                                    name="competence_date"
                                    render={({ field }) => (
                                      <FormItem className="space-y-2">
                                        <FormLabel className="text-sm font-semibold flex items-center gap-2">
                                          <Calendar className="h-3.5 w-3.5 text-purple-500" />
                                          Competência
                                        </FormLabel>
                                        <FormControl>
                                          <EnhancedDatePicker
                                            value={field.value}
                                            onChange={handleDateChange("competence_date")}
                                            placeholder="Selecione"
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Status Card */}
                          <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                            <CardHeader className="p-4 pb-2">
                              <CardTitle className="text-lg font-bold flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-primary" />
                                Status
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 pt-2">
                              <TransactionStatusSelector />
                            </CardContent>
                          </Card>
                        </div>
                      </div>

                      {/* Attachments & Notes Card - Combined with tabs */}
                      <Card className="shadow-sm border-0 bg-card/50 backdrop-blur-sm">
                        <CardContent className="p-4">
                          <Tabs defaultValue="anexos" className="w-full">
                            <TabsList className="w-full grid grid-cols-2 h-10">
                              <TabsTrigger value="anexos" className="flex items-center gap-2 data-[state=active]:bg-background">
                                <Paperclip className="h-4 w-4" />
                                Anexos
                              </TabsTrigger>
                              <TabsTrigger value="observacoes" className="flex items-center gap-2 data-[state=active]:bg-background">
                                <FileText className="h-4 w-4" />
                                Observações
                              </TabsTrigger>
                            </TabsList>
                            <TabsContent value="anexos" className="mt-4">
                              <AttachmentsSection
                                attachments={attachments.map(att => ({
                                  file: att.file,
                                  description: att.description,
                                  attachment_form: (att.attachment_form as any) || 'receipt',
                                  attachment_type: (att.attachment_type as any) || 'document'
                                }))}
                                setAttachments={setAttachments}
                                existingAttachments={existingAttachments}
                                handleDeleteExistingAttachment={handleDeleteExistingAttachment}
                                handleDownloadAttachment={handleDownloadAttachment}
                                onExistingAttachmentDescriptionChange={handleExistingAttachmentDescriptionChange}
                              />
                            </TabsContent>
                            <TabsContent value="observacoes" className="mt-4">
                              <FormField
                                control={form.control}
                                name="notes"
                                render={({ field }) => (
                                  <FormItem className="space-y-2">
                                    <FormControl>
                                      <Textarea
                                        placeholder="Adicione observações detalhadas sobre esta transação..."
                                        className="min-h-[120px] text-sm resize-y rounded-lg border-2 focus-visible:ring-2 focus-visible:ring-primary/20"
                                        {...field}
                                        readOnly={!isEditing}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </TabsContent>
                          </Tabs>
                        </CardContent>
                      </Card>


                    </div>
                  </ScrollArea>
                </form>
            </TransactionErrorBoundary>
          </div>
        </div>

        {/* Modals para adicionar cliente/fornecedor */}
        <QuickAddClientModal
          isOpen={showAddClientModal}
          onClose={() => setShowAddClientModal(false)}
          onClientAdded={handleClientAdded}
        />

        <QuickAddSupplierModal
          isOpen={showAddSupplierModal}
          onClose={() => setShowAddSupplierModal(false)}
          onSupplierAdded={handleSupplierAdded}
        />

        <QuickAddCostCenterModal
          isOpen={showAddCostCenterModal}
          onClose={() => setShowAddCostCenterModal(false)}
          onCostCenterAdded={handleCostCenterAdded}
        />

        <QuickAddPaymentAccountModal
          isOpen={showAddPaymentAccountModal}
          onClose={() => setShowAddPaymentAccountModal(false)}
          onPaymentAccountAdded={handlePaymentAccountAdded}
        />
      </Form>
      </DialogContent>
    </Dialog>
  );
}
